import { Component, OnInit, HostListener } from "@angular/core";

import { CloudGatewayView } from "@impulse/sdp-shared-models";

import { BrowserTitleService } from "../../common/browser-title/browser-title.service";
import { GatewaysService, GatewayViews } from "./gateways.service";
import { supportEmail } from "../../util";
import { GatewayView } from "./gateway-view";
import { TranslateService } from "../../common/translate/translate.service";
import { SnackbarService } from "../../common/snackbar/snackbar.service";
import { GatewaysUiService } from "./gateways.ui.service";
import { AccountInfoService } from "../../common/account-info/account-info-service";

type GatewayType = "cloud" | "private" | "reset";

@Component({
	templateUrl: "./gateways.component.html"
})
export class GatewaysComponent implements OnInit {

	public gatewaysData: GatewayView[] | undefined = undefined;
	public pendingGatewaysData: GatewayView[] | undefined = undefined;
	public errorMessage: string | undefined = undefined;
	public supportEmail = supportEmail;
	public selectedGatewayType: GatewayType = "cloud";
	public cloudGateway: CloudGatewayView = { host: "", type: "cloud" };
	public warnIcon = {
		name: "warning",
		type: "material",
		cssClass: "sdp-device-list-item-warn"
	};
	public hasData = false;
	public noActiveGateways = false;

	public accountInfoPending = true;
	public isPrivateGatewayFeatureFlagEnabled = false;

	public isDataLoaded = false;

	constructor(
		private service: GatewaysService,
		private titleService: BrowserTitleService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService,
		private uiService: GatewaysUiService,
		private accountInfoSerivce: AccountInfoService
	) { }

	public get hasPrivateGateways() {
		return !!this.gatewaysData && this.gatewaysData.length > 0;
	}

	public get disabledText() {
		return this.translateService.translate("gateway.description.private.disabled");
	}

	@HostListener("window:beforeunload", ["$event"])
	public unloadNotification($event: any) {
		return this.confirmLeavePrivate();
	}

	public async ngOnInit() {
		this.titleService.setTitle("gateways.title");
		this.service.reloadGateways.subscribe((data: GatewayViews) => {
			this.cloudGateway = data.cloudGateway;
			this.filterData(data.gatewayViews);
			this.noActiveGateways = this.checkForNoActiveGateways();
		});
		this.service.gatewayTypeSelect.subscribe((value: GatewayType) => {
			this.selectedGatewayType = value;
		});

		await this.refresh();
	}

	public async makeCloudActive() {
		// return new Promise<boolean>(async (resolve, reject) => {
		// only activate if switching from private to cloud
		// must check for active state in privategateways, if you are on cloud, select private, then go back to cloud
		// and only check selectedGatewayType you will get the dialog for turning on cloud again.
		if (!!this.gatewaysData && this.gatewaysData.find((g) => { return g.state === "active"; })) {
			try {
				if (await this.uiService.setActiveGateway("cloud")) {
					this.snackbarService.open(this.translateService.translate("sdp.gateway.activated", {
						label: "Cloud"
					}));
				}
			} catch (e) {
				this.snackbarService.showErrorBar(e, { label: "Cloud" });
			}
		}
	}

	public async makePrivateActive() {
		if (this.gatewaysData && this.gatewaysData.length === 1) {
			// if only one private go ahead and try to activate
			const row = this.gatewaysData[0];
			try {
				await this.uiService.setActiveGateway(row, true);
				this.snackbarService.open(this.translateService.translate("sdp.gateway.activated", {
					label: row.label
				}));
			} catch (e) {
				this.snackbarService.showErrorBar(e, { label: row.label });
			}
		} else if (!this.gatewaysData || this.gatewaysData.find((g) => { return g.state === "active"; })) {
			// set radio to private, no activate, it will be done by gateway toggle state
			// there is no event being emitted when selecting the private radio button
			this.selectedGatewayType = "private";
		}
	}

	public async refresh() {
		// reset data back to default states
		this.hasData = false;
		this.errorMessage = undefined;
		this.gatewaysData = undefined;
		this.accountInfoPending = true;
		this.isDataLoaded = false;

		// service calls to get new data
		const accountInfoPromise = this.accountInfoSerivce.getAccountInfo();
		const getGatewaysPromise = this.service.getGateways();

		try {
			const accountInfo = await accountInfoPromise;
			this.isPrivateGatewayFeatureFlagEnabled = accountInfo.limits.isPrivateGatewayEnabled;
		} catch {
			// error handling already happened in the service
			this.isPrivateGatewayFeatureFlagEnabled = false;
		} finally {
			this.accountInfoPending = false;
		}

		try {
			await getGatewaysPromise;
		} catch (e) {
			const message = this.translateService.translate("error.api.generic.message");
			this.errorMessage = message;
		} finally {
			this.isDataLoaded = true;
		}
	}

	public checkForNoActiveGateways() {
		return !this.gatewaysData || !this.gatewaysData.find((gateway) => {
			return gateway.state === "active";
		});
	}

	public confirmLeavePrivate() {
		if (this.selectedGatewayType === "private" && this.noActiveGateways) {
			// no custom message will be displayed unless fired from AuthGuard going to another route
			// https://www.chromestatus.com/feature/5349061406228480 true for ie edge and ff as well
			if (confirm(this.translateService.translate("sdp.beforeunload.message"))) {
				return true;
			} else {
				return false;
			}
		}
		return true;
	}

	private filterData(data: GatewayView[]) {
		// split data based on state, pending vs rest
		this.gatewaysData = data.filter((gateway) => { return gateway.state !== "pending" && gateway.state !== "provisioning"; });
		this.pendingGatewaysData = data.filter((gateway) => { return this.service.isGatewayPending(gateway); });
		this.hasData = true;
	}

}
